import { motion } from "framer-motion";
import { FileText, Eye, CheckCircle2 } from "lucide-react";

const LUNETTES = [
    { age: "Moins de 16 ans", duree: "1 an" },
    { age: "De 16 à 42 ans", duree: "5 ans" },
    { age: "Plus de 42 ans", duree: "3 ans" },
];
const LENTILLES = [
    { age: "Moins de 16 ans", duree: "1 an" },
    { age: "Plus de 16 ans", duree: "5 ans" },
];

function DureeBlock({ titre, items, accent, icon: Icon }) {
    return (
        <div className="bg-lpl-bg border border-lpl-border p-7 md:p-8">
            <div className="flex items-center gap-3 mb-6">
                <span
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: accent, color: "#0F2A20" }}
                >
                    <Icon size={18} strokeWidth={1.8} />
                </span>
                <h3 className="font-display font-semibold text-2xl md:text-3xl tracking-tight">
                    {titre}
                </h3>
            </div>
            <ul className="space-y-3">
                {items.map((it) => (
                    <li
                        key={it.age}
                        className="flex items-baseline justify-between gap-4 border-b border-dashed border-lpl-border pb-3 last:border-0 last:pb-0"
                    >
                        <span className="text-sm md:text-base text-lpl-text-muted">
                            {it.age}
                        </span>
                        <span
                            className="font-display font-semibold text-lg md:text-xl tracking-tight"
                            style={{ color: "#3D6E5B" }}
                        >
                            {it.duree}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default function ValiditeOrdonnance() {
    return (
        <section
            id="validite-ordonnance"
            className="relative py-20 md:py-28 bg-lpl-bg-alt overflow-hidden"
            data-testid="validite-ordonnance-section"
        >
            <div className="max-w-7xl mx-auto px-5 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.6 }}
                    className="mb-12 max-w-3xl"
                >
                    <p className="font-serif italic text-xl md:text-2xl text-lpl-terracotta mb-3">
                        Bon à savoir
                    </p>
                    <h2 className="font-display font-semibold text-4xl md:text-6xl text-lpl-text leading-[0.95] tracking-tight">
                        Validité d'une{" "}
                        <span className="font-serif italic font-normal text-lpl-primary-deep">
                            ordonnance
                        </span>
                        <span className="font-serif italic font-normal text-lpl-lavender">
                            .
                        </span>
                    </h2>
                    <p className="mt-5 text-base md:text-lg text-lpl-text-muted leading-relaxed">
                        Si vous avez une ordonnance en cours de validité et plus de 16 ans,
                        votre opticien peut adapter la correction de vos verres et en informer
                        votre ophtalmologiste. Voici les durées légales à retenir.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-7">
                    <DureeBlock
                        titre="Pour les lunettes"
                        items={LUNETTES}
                        accent="#F2D580"
                        icon={FileText}
                    />
                    <DureeBlock
                        titre="Pour les lentilles"
                        items={LENTILLES}
                        accent="#ECB6FF"
                        icon={Eye}
                    />
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="mt-8 md:mt-10 relative bg-lpl-primary-deep text-lpl-bg p-7 md:p-10 overflow-hidden"
                >
                    <div
                        aria-hidden
                        className="pointer-events-none absolute -bottom-12 -right-12 font-serif italic text-[14rem] leading-none text-lpl-bg/[0.05] select-none"
                    >
                        +16
                    </div>
                    <div className="relative flex flex-col md:flex-row md:items-start gap-5">
                        <span className="shrink-0 w-12 h-12 rounded-full bg-lpl-yellow text-lpl-text flex items-center justify-center">
                            <CheckCircle2 size={20} strokeWidth={2} />
                        </span>
                        <div>
                            <p className="font-display font-semibold text-xl md:text-2xl leading-tight tracking-tight mb-3">
                                Pas envie d'attendre des mois chez l'ophtalmo ?
                            </p>
                            <p className="text-base md:text-lg leading-relaxed text-lpl-bg/85 max-w-3xl">
                                Si vous êtes en possession d'une ordonnance en cours de
                                validité et que vous avez plus de 16 ans, vous pouvez
                                demander un contrôle de vue directement chez votre
                                opticien. Si besoin, on adapte votre correction et on
                                informe votre ophtalmologiste — tranquillement.
                            </p>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
